import { VerticalTabs } from "@components/common/tabs/vertical-tabs";
import { MainPageLayout } from "@components/page-layout/main-page-layout";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Box } from "@mui/material";
import { AccountSettingsMobileLayout } from "@pages/account-settings/account-settings-layout/mobile-layout/account-settings-mobile-layout";
import { useAccountSettingsTabs } from "@pages/account-settings/account-settings-utils";
import { CloseAccountSettingsButton } from "@pages/account-settings/close-account-settings-button";
import { NotFoundPage } from "@pages/not-found-page";
import { AccountSettingsTabs } from "@router/route-params";
import { useAppParams } from "@router/router-helper";
import { BrowserUtils } from "@stellar/utils";
import { useAppSelector } from "@store/store-helper";
import { fetchingUserFlagsSelector } from "@store/user/user-selector";
import { OpenTabEvents } from "@utils/track-event/track-event-list";

/** The layout of account settings */
export function AccountSettingsLayout(): JSX.Element {
  const { isScreenSmOrSmaller } = useMediaQueryList();
  const { loggedInUserFetchingStatus } = useAppSelector(fetchingUserFlagsSelector);
  const { accountSettingsTabs } = useAccountSettingsTabs();
  const { accountSettingsTab } = useAppParams();

  // If accountSettingsTab is available, but not valid, show not found page
  // If accountSettingsTab is not available (undefined), then:
  // - If media is mobile, show the tab selection page
  // - If media is desktop, show the default tab
  if (
    accountSettingsTab &&
    !Object.values(AccountSettingsTabs).includes(accountSettingsTab)
  ) {
    return <NotFoundPage />;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
      }}
    >
      {isScreenSmOrSmaller ? (
        <AccountSettingsMobileLayout
          tabs={accountSettingsTabs}
          selectedTabId={accountSettingsTab}
        />
      ) : (
        accountSettingsTab && (
          <MainPageLayout
            shouldHideOpenButton={true}
            shouldShowSidebar={false}
            shouldShowTopbarBorder={true}
            topLeftHeaderButton={<CloseAccountSettingsButton />}
            shouldUseFullBodyLayout={true}
            shouldShowProgressOverviewMenu={!BrowserUtils.isInIframe()}
          >
            <VerticalTabs
              tabs={accountSettingsTabs}
              tabSelectionHeader="Settings"
              selectedTabId={accountSettingsTab}
              isLoading={loggedInUserFetchingStatus === "pending"}
              trackingEventPageName={OpenTabEvents.openAccountSettingsTab}
              dataTestId="account-settings-tab"
            />
          </MainPageLayout>
        )
      )}
    </Box>
  );
}
